<script setup>
import { reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

const GREETING_VALUES = ['assalamualaikum', 'hi', 'hello'];
const HONORIFIC_VALUES = ['sir', 'bhai', 'apa', 'none'];
const LANGUAGE_MODES = ['locked', 'mirror'];
const DEFAULT_LANGUAGES = ['bangla', 'banglish', 'english'];
const TONES = ['friendly', 'formal', 'casual'];
const ADDRESS_FORMS = ['apni', 'tumi'];
const SALES_STYLES = [
  'quote_first',
  'qualify_then_quote',
  'consultative',
  'never_confirm',
];
const PHOTO_AFTER_MATCH = ['quote', 'clarify_variant', 'ask_need', 'handoff'];
const GREETING_PREVIEW = {
  assalamualaikum: { banglish: 'Assalamu alaikum', bangla: 'আসসালামু আলাইকুম' },
  hi: { banglish: 'Hi', bangla: 'হাই' },
  hello: { banglish: 'Hello', bangla: 'হ্যালো' },
};
const HONORIFIC_PREVIEW = {
  sir: { banglish: 'sir', bangla: 'স্যার' },
  bhai: { banglish: 'bhai', bangla: 'ভাই' },
  apa: { banglish: 'apa', bangla: 'আপা' },
  none: { banglish: '', bangla: '' },
};

const state = reactive({
  languageMode: 'mirror',
  defaultLanguage: 'banglish',
  tone: 'friendly',
  addressForm: 'apni',
  salesStyle: 'quote_first',
  photoAfterMatch: 'quote',
  askOrderAfterQuote: true,
  allowConfirmOrder: true,
  scheduleMode: 'always',
  outsideHoursAction: 'handoff',
  replyWaitSeconds: 0,
  greeting: 'assalamualaikum',
  honorific: 'sir',
  burstEnabled: true,
  burstLevel: 'normal',
  typingMinSeconds: 2,
  typingMaxSeconds: 8,
  handoffMessage: '',
  onHumanRequest: true,
  onMissingInfo: true,
  repeatLimit: 3,
  onComplaint: true,
  onOutOfStock: true,
  onConfirmFail: true,
});

const languageModeOptions = computed(() => [
  {
    value: 'locked',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.MODE.LOCKED'),
  },
  {
    value: 'mirror',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.MODE.MIRROR'),
  },
]);

const defaultLanguageOptions = computed(() => [
  {
    value: 'bangla',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.DEFAULT.BANGLA'),
  },
  {
    value: 'banglish',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.DEFAULT.BANGLISH'),
  },
  {
    value: 'english',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.DEFAULT.ENGLISH'),
  },
]);

const toneOptions = computed(() => [
  {
    value: 'friendly',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.TONE.FRIENDLY'),
  },
  {
    value: 'formal',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.TONE.FORMAL'),
  },
  {
    value: 'casual',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.TONE.CASUAL'),
  },
]);

const addressFormOptions = computed(() => [
  {
    value: 'apni',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.ADDRESS.APNI'),
  },
  {
    value: 'tumi',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.ADDRESS.TUMI'),
  },
]);

const salesStyleOptions = computed(() => [
  {
    value: 'quote_first',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.STYLE.QUOTE_FIRST'),
  },
  {
    value: 'qualify_then_quote',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.STYLE.QUALIFY_THEN_QUOTE'),
  },
  {
    value: 'consultative',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.STYLE.CONSULTATIVE'),
  },
  {
    value: 'never_confirm',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.STYLE.NEVER_CONFIRM'),
  },
]);

const photoAfterMatchOptions = computed(() => [
  {
    value: 'quote',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.PHOTO.QUOTE'),
  },
  {
    value: 'clarify_variant',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.PHOTO.CLARIFY_VARIANT'),
  },
  {
    value: 'ask_need',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.PHOTO.ASK_NEED'),
  },
  {
    value: 'handoff',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.PHOTO.HANDOFF'),
  },
]);

const scheduleOptions = computed(() => [
  {
    value: 'always',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SCHEDULE.ALWAYS'),
  },
  {
    value: 'inbox_hours',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SCHEDULE.INBOX_HOURS'),
  },
]);

const outsideHoursOptions = computed(() => [
  {
    value: 'handoff',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.OUTSIDE_HOURS.HANDOFF'),
  },
  {
    value: 'wait',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.OUTSIDE_HOURS.WAIT'),
  },
]);

const waitPresets = computed(() => [
  { value: 0, label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.WAIT.INSTANT') },
  { value: 5, label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.WAIT.FIVE_SECONDS') },
  {
    value: 15,
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.WAIT.FIFTEEN_SECONDS'),
  },
  {
    value: 30,
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.WAIT.THIRTY_SECONDS'),
  },
  { value: 60, label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.WAIT.ONE_MINUTE') },
  { value: 120, label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.WAIT.TWO_MINUTES') },
  {
    value: 300,
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.WAIT.FIVE_MINUTES'),
  },
]);

const burstLevelOptions = computed(() => [
  {
    value: 'short',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.BURST.LEVEL.SHORT'),
  },
  {
    value: 'normal',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.BURST.LEVEL.NORMAL'),
  },
  {
    value: 'single',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.BURST.LEVEL.SINGLE'),
  },
]);

const greetingOptions = computed(() => [
  {
    value: 'assalamualaikum',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.GREET.STYLE.ASSALAMUALAIKUM'),
  },
  {
    value: 'hi',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.GREET.STYLE.HI'),
  },
  {
    value: 'hello',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.GREET.STYLE.HELLO'),
  },
]);

const honorificOptions = computed(() => [
  {
    value: 'sir',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.GREET.HONORIFIC.SIR'),
  },
  {
    value: 'bhai',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.GREET.HONORIFIC.BHAI'),
  },
  {
    value: 'apa',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.GREET.HONORIFIC.APA'),
  },
  {
    value: 'none',
    label: t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.GREET.HONORIFIC.NONE'),
  },
]);

const greetingPreview = computed(() => {
  const greet =
    GREETING_PREVIEW[state.greeting] || GREETING_PREVIEW.assalamualaikum;
  const honor = HONORIFIC_PREVIEW[state.honorific] || HONORIFIC_PREVIEW.sir;
  const banglish = honor.banglish
    ? `${greet.banglish} ${honor.banglish}.`
    : `${greet.banglish}.`;
  const bangla = honor.bangla
    ? `${greet.bangla} ${honor.bangla}।`
    : `${greet.bangla}।`;
  return { banglish, bangla };
});

const selectClass =
  'block w-full reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 h-10 px-3 text-n-slate-12 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand';

const inferLanguageMode = (behavior, brainLanguage) => {
  if (LANGUAGE_MODES.includes(behavior.language_mode)) {
    return behavior.language_mode;
  }
  if (!brainLanguage || brainLanguage === 'auto') return 'mirror';
  return 'locked';
};

const inferDefaultLanguage = (behavior, brainLanguage) => {
  if (DEFAULT_LANGUAGES.includes(behavior.default_language)) {
    return behavior.default_language;
  }
  if (brainLanguage === 'bangla') return 'bangla';
  if (brainLanguage === 'english') return 'english';
  return 'banglish';
};

const inferTone = (behavior, brainTone) => {
  if (TONES.includes(behavior.tone)) return behavior.tone;
  if (brainTone === 'formal') return 'formal';
  if (brainTone === 'casual') return 'casual';
  return 'friendly';
};

const updateStateFromAssistant = assistant => {
  const behavior = assistant?.config?.behavior || {};
  const brain = assistant?.config?.business_brain || {};
  const handoff = behavior.handoff || {};
  const brainLanguage = brain.language || '';
  state.languageMode = inferLanguageMode(behavior, brainLanguage);
  state.defaultLanguage = inferDefaultLanguage(behavior, brainLanguage);
  state.tone = inferTone(behavior, brain.tone);
  state.addressForm = ADDRESS_FORMS.includes(behavior.address_form)
    ? behavior.address_form
    : 'apni';
  state.salesStyle = SALES_STYLES.includes(behavior.sales_style)
    ? behavior.sales_style
    : 'quote_first';
  state.photoAfterMatch = PHOTO_AFTER_MATCH.includes(behavior.photo_after_match)
    ? behavior.photo_after_match
    : 'quote';
  state.askOrderAfterQuote = behavior.ask_order_after_quote !== false;
  state.allowConfirmOrder = behavior.allow_confirm_order !== false;
  state.scheduleMode = behavior.schedule_mode || 'always';
  state.outsideHoursAction = behavior.outside_hours_action || 'handoff';
  state.greeting = GREETING_VALUES.includes(behavior.greeting)
    ? behavior.greeting
    : 'assalamualaikum';
  state.honorific = HONORIFIC_VALUES.includes(behavior.honorific)
    ? behavior.honorific
    : 'sir';
  state.replyWaitSeconds =
    behavior.reply_wait_seconds === undefined ||
    behavior.reply_wait_seconds === null
      ? 0
      : Number(behavior.reply_wait_seconds);
  state.typingMinSeconds =
    behavior.typing_min_seconds === undefined ||
    behavior.typing_min_seconds === null
      ? 2
      : Number(behavior.typing_min_seconds);
  state.typingMaxSeconds =
    behavior.typing_max_seconds === undefined ||
    behavior.typing_max_seconds === null
      ? 8
      : Number(behavior.typing_max_seconds);
  const burst = behavior.burst || {};
  state.burstEnabled =
    burst.enabled === undefined || burst.enabled === null
      ? true
      : burst.enabled !== false;
  state.burstLevel = ['short', 'normal', 'single'].includes(burst.level)
    ? burst.level
    : 'normal';
  state.onHumanRequest = handoff.on_human_request !== false;
  state.onMissingInfo = handoff.on_missing_info !== false;
  state.repeatLimit =
    handoff.repeat_limit === undefined || handoff.repeat_limit === null
      ? 3
      : Number(handoff.repeat_limit);
  state.onComplaint = handoff.on_complaint !== false;
  state.onOutOfStock = handoff.on_out_of_stock !== false;
  state.onConfirmFail = handoff.on_confirm_fail !== false;
  state.handoffMessage =
    handoff.message || assistant?.config?.handoff_message || '';
};

const handleSubmit = () => {
  const min = Number(state.typingMinSeconds) || 0;
  const max = Math.max(Number(state.typingMaxSeconds) || 0, min);

  emit('submit', {
    config: {
      handoff_message: state.handoffMessage,
      behavior: {
        language_mode: state.languageMode,
        default_language: state.defaultLanguage,
        tone: state.tone,
        address_form: state.addressForm,
        sales_style: state.salesStyle,
        photo_after_match: state.photoAfterMatch,
        ask_order_after_quote: state.askOrderAfterQuote,
        allow_confirm_order: state.allowConfirmOrder,
        schedule_mode: state.scheduleMode,
        outside_hours_action: state.outsideHoursAction,
        greeting: state.greeting,
        honorific: state.honorific,
        reply_wait_seconds: Number(state.replyWaitSeconds) || 0,
        typing_min_seconds: min,
        typing_max_seconds: max,
        burst: {
          enabled: state.burstEnabled,
          level: state.burstLevel,
        },
        handoff: {
          message: state.handoffMessage,
          on_human_request: state.onHumanRequest,
          on_missing_info: state.onMissingInfo,
          repeat_limit: Number(state.repeatLimit) || 0,
          on_complaint: state.onComplaint,
          on_out_of_stock: state.onOutOfStock,
          on_confirm_fail: state.onConfirmFail,
        },
      },
    },
  });
};

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <section
      class="flex flex-col gap-4 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <h4 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.CARDS.LANGUAGE') }}
      </h4>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.MODE.LABEL') }}
          </label>
          <select v-model="state.languageMode" :class="selectClass">
            <option
              v-for="option in languageModeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <p class="mt-1 mb-0 text-label-small text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.MODE.HELP') }}
          </p>
        </div>
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.DEFAULT.LABEL') }}
          </label>
          <select v-model="state.defaultLanguage" :class="selectClass">
            <option
              v-for="option in defaultLanguageOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.TONE.LABEL') }}
          </label>
          <select v-model="state.tone" :class="selectClass">
            <option
              v-for="option in toneOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.ADDRESS.LABEL') }}
          </label>
          <select v-model="state.addressForm" :class="selectClass">
            <option
              v-for="option in addressFormOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <p class="mt-1 mb-0 text-label-small text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.LANGUAGE.ADDRESS.HELP') }}
          </p>
        </div>
      </div>
    </section>

    <section
      class="flex flex-col gap-4 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <h4 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.CARDS.SALES') }}
      </h4>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.STYLE.LABEL') }}
          </label>
          <select v-model="state.salesStyle" :class="selectClass">
            <option
              v-for="option in salesStyleOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <p class="mt-1 mb-0 text-label-small text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.STYLE.HELP') }}
          </p>
        </div>
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.PHOTO.LABEL') }}
          </label>
          <select v-model="state.photoAfterMatch" :class="selectClass">
            <option
              v-for="option in photoAfterMatchOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <p class="mt-1 mb-0 text-label-small text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.PHOTO.HELP') }}
          </p>
        </div>
      </div>
      <label class="flex items-center justify-between gap-4">
        <span class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.ASK_ORDER.LABEL') }}
        </span>
        <Switch v-model="state.askOrderAfterQuote" />
      </label>
      <p class="mt-0 mb-0 text-label-small text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.ASK_ORDER.HELP') }}
      </p>
      <label class="flex items-center justify-between gap-4">
        <span class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.ALLOW_CONFIRM.LABEL') }}
        </span>
        <Switch v-model="state.allowConfirmOrder" />
      </label>
      <p class="mt-0 mb-0 text-label-small text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SALES.ALLOW_CONFIRM.HELP') }}
      </p>
    </section>

    <section
      class="flex flex-col gap-4 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <h4 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.CARDS.WHEN') }}
      </h4>
      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SCHEDULE.LABEL') }}
        </label>
        <select v-model="state.scheduleMode" :class="selectClass">
          <option
            v-for="option in scheduleOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <p class="mt-1 mb-0 text-label-small text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.SCHEDULE.HELP') }}
        </p>
      </div>

      <div
        v-if="state.scheduleMode === 'inbox_hours'"
        class="flex flex-col gap-1"
      >
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.OUTSIDE_HOURS.LABEL') }}
        </label>
        <select v-model="state.outsideHoursAction" :class="selectClass">
          <option
            v-for="option in outsideHoursOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>
    </section>

    <section
      class="flex flex-col gap-4 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <h4 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.CARDS.FEEL') }}
      </h4>
      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.GREET.TITLE') }}
        </label>
        <p class="mt-0 mb-1 text-label-small text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.GREET.HELP') }}
        </p>
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.GREET.STYLE.LABEL') }}
          </label>
          <select v-model="state.greeting" :class="selectClass">
            <option
              v-for="option in greetingOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.GREET.HONORIFIC.LABEL') }}
          </label>
          <select v-model="state.honorific" :class="selectClass">
            <option
              v-for="option in honorificOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>
      <p class="mt-0 mb-0 text-label-small text-n-slate-11">
        {{
          t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.GREET.PREVIEW', {
            banglish: greetingPreview.banglish,
            bangla: greetingPreview.bangla,
          })
        }}
      </p>
      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.WAIT.LABEL') }}
        </label>
        <select v-model.number="state.replyWaitSeconds" :class="selectClass">
          <option
            v-for="option in waitPresets"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <p class="mt-1 mb-0 text-label-small text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.WAIT.HELP') }}
        </p>
      </div>

      <label class="flex items-center justify-between gap-4">
        <span class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.BURST.LABEL') }}
        </span>
        <Switch v-model="state.burstEnabled" />
      </label>

      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.BURST.LEVEL.LABEL') }}
        </label>
        <select v-model="state.burstLevel" :class="selectClass">
          <option
            v-for="option in burstLevelOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>

      <div v-if="state.burstEnabled" class="flex flex-col gap-2">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            v-model="state.typingMinSeconds"
            type="number"
            :label="t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.TYPING_MIN.LABEL')"
            :placeholder="
              t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.TYPING_MIN.PLACEHOLDER')
            "
          />
          <Input
            v-model="state.typingMaxSeconds"
            type="number"
            :label="t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.TYPING_MAX.LABEL')"
            :placeholder="
              t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.TYPING_MAX.PLACEHOLDER')
            "
          />
        </div>
        <p class="mb-0 text-label-small text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.TYPING.HELP') }}
        </p>
      </div>
    </section>

    <section
      class="flex flex-col gap-4 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <h4 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.CARDS.HANDOFF') }}
      </h4>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.HANDOFF.TITLE') }}
      </p>
      <TextArea
        v-model="state.handoffMessage"
        :label="t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.HANDOFF.MESSAGE.LABEL')"
        :placeholder="
          t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.HANDOFF.MESSAGE.PLACEHOLDER')
        "
        :max-length="2000"
        auto-height
      />
      <p class="mb-0 text-label-small text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.HANDOFF.MESSAGE.HELP') }}
      </p>
      <label class="flex items-center justify-between gap-4">
        <span class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.HANDOFF.HUMAN_REQUEST') }}
        </span>
        <Switch v-model="state.onHumanRequest" />
      </label>
      <label class="flex items-center justify-between gap-4">
        <span class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.HANDOFF.MISSING_INFO') }}
        </span>
        <Switch v-model="state.onMissingInfo" />
      </label>
      <label class="flex items-center justify-between gap-4">
        <span class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.HANDOFF.COMPLAINT') }}
        </span>
        <Switch v-model="state.onComplaint" />
      </label>
      <label class="flex items-center justify-between gap-4">
        <span class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.HANDOFF.OUT_OF_STOCK') }}
        </span>
        <Switch v-model="state.onOutOfStock" />
      </label>
      <label class="flex items-center justify-between gap-4">
        <span class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.HANDOFF.CONFIRM_FAIL') }}
        </span>
        <Switch v-model="state.onConfirmFail" />
      </label>
      <div class="flex flex-col gap-2">
        <Input
          v-model="state.repeatLimit"
          type="number"
          :label="t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.HANDOFF.REPEAT.LABEL')"
          :placeholder="
            t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.HANDOFF.REPEAT.PLACEHOLDER')
          "
        />
        <p class="mb-0 text-label-small text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.HANDOFF.REPEAT.HELP') }}
        </p>
      </div>
    </section>

    <div>
      <Button
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
